import React, { useState, useEffect, useCallback } from 'react';
import { dashboardAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Calendar, Video, Trash2, Plus, CalendarX } from 'lucide-react';
import MeetingScheduler from './MeetingScheduler';
import { describeMeetingTime } from '../utils/timezones';

/**
 * "Scheduled meeting 1, 2, 3…" on the pre-sales form — the same shape as the
 * follow-up dates right below it, but each row is a real Outlook meeting: it
 * lands in the BDA's own calendar with a Teams link and the client gets the
 * usual invitation.
 *
 * Self-contained on purpose: it owns its own fetching and saving so the lead
 * form does not have to carry meeting state through its save cycle. Meetings
 * need a saved lead to hang off, so before the lead exists it just says so.
 */
const LeadMeetingsSection = ({ leadId, companyName, bdeName, pocEmails = [] }) => {
  const { user } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const flash = (type, text, ms = 6000) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), ms);
  };

  const fetchMeetings = useCallback(async () => {
    if (!leadId) return;
    try {
      setLoading(true);
      const res = await dashboardAPI.getMeetings({ lead_id: leadId });
      if (res.success) setMeetings(res.data || []);
    } catch (e) {
      console.error('Could not load meetings:', e.message);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { fetchMeetings(); }, [fetchMeetings]);

  // Calling it off tells the attendees and keeps the record.
  const cancelMeeting = async (m) => {
    const comment = window.prompt(
      `Cancel meeting ${m.meeting_number}?\n\nThe attendees are notified. Add a note for them (optional):`, ''
    );
    if (comment === null) return;
    try {
      const res = await dashboardAPI.cancelMeeting(m.id, comment);
      if (res.success) { flash(res.warning ? 'error' : 'success', res.warning || res.message || 'Meeting cancelled.'); fetchMeetings(); }
      else flash('error', res.error || 'Could not cancel.');
    } catch (e) {
      flash('error', e.response?.data?.error || e.message);
    }
  };

  // Removing the record entirely, for a mistake rather than a cancellation.
  const removeMeeting = async (m) => {
    if (!window.confirm(`Delete the record of meeting ${m.meeting_number}? Use Cancel if the attendees still need telling.`)) return;
    try {
      const res = await dashboardAPI.deleteMeeting(m.id);
      if (res.success) { flash('success', 'Meeting removed.'); fetchMeetings(); }
      else flash('error', res.error || 'Could not remove.');
    } catch (e) {
      flash('error', e.response?.data?.error || e.message);
    }
  };

  return (
    <div className="mt-6 pt-6 border-t border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2">
          <Calendar size={18} className="text-[#679632]" /> Meeting Schedule
        </h3>
        {leadId && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1 px-3 py-1.5 bg-[#679632] text-white rounded-lg text-xs font-medium hover:bg-[#587f2b]"
          >
            <Plus size={14} /> Schedule a meeting
          </button>
        )}
      </div>

      {!leadId && (
        <p className="text-sm text-gray-500">Save the lead first, then you can schedule meetings against it.</p>
      )}

      {message.text && (
        <div className={`mb-3 p-2.5 rounded-lg text-xs ${message.type === 'error' ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'}`}>
          {message.text}
        </div>
      )}

      {leadId && (
        loading ? (
          <p className="text-sm text-gray-500">Loading meetings…</p>
        ) : meetings.length === 0 ? (
          <p className="text-sm text-gray-500">No meetings scheduled yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {meetings.map(m => (
              <div key={m.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50/60">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Scheduled meeting {m.meeting_number}
                      {(m.status || '') === 'Cancelled' && (
                        <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-100">cancelled</span>
                      )}
                    </p>
                    <p className="text-xs text-gray-600">{m.meeting_title}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {(m.status || '') !== 'Cancelled' && (
                      <button
                        type="button"
                        onClick={() => cancelMeeting(m)}
                        className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                        title="Call it off and notify the attendees"
                      >
                        <CalendarX size={14} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeMeeting(m)}
                      className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Remove this record"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <p className="mt-2 text-sm text-gray-800">
                  {m.scheduled_at
                    ? new Date(m.scheduled_at).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                    : '—'}
                  <span className="text-xs text-gray-500"> · {m.duration_minutes || 30} min</span>
                </p>
                {m.time_zone && (
                  <p className="text-[11px] text-gray-400">booked as {describeMeetingTime(m.scheduled_at, m.time_zone)}</p>
                )}
                {m.agenda && <p className="mt-1 text-xs text-gray-600 line-clamp-2">{m.agenda}</p>}
                {(m.attendees || []).length > 0 && (
                  <p className="mt-1 text-xs text-gray-500 truncate">
                    {(m.attendees || []).map(a => a.email || a).join(', ')}
                  </p>
                )}
                {m.join_url && (
                  <a
                    href={m.join_url} target="_blank" rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-xs text-[#0f6cbd] hover:underline"
                  >
                    <Video size={13} /> Join meeting link
                  </a>
                )}
              </div>
            ))}
          </div>
        )
      )}

      {showForm && (
        <div className="mt-4">
          <MeetingScheduler
            leadId={leadId}
            companyName={companyName}
            onScheduled={() => { setShowForm(false); fetchMeetings(); }}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}
    </div>
  );
};

export default LeadMeetingsSection;
